import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Package,
  Plus,
  Archive,
  ArchiveRestore,
  Pencil,
  Search,
} from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useCompany } from '../../context/CompanyContext';
import { useRole } from '../../context/RoleContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useCatalogItems, invalidateCatalog } from '../../hooks/useCatalogItems';

const API = process.env.REACT_APP_BACKEND_URL;

// Suggested (non-restrictive) units — free text is also allowed.
const UNIT_SUGGESTIONS = ['ea', 'oz', 'lb', 'gal', 'ft', 'hr', 'sq ft'];

async function getAuthHeader() {
  const token = await getAuth().currentUser?.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

const emptyForm = () => ({
  id: null,
  name: '',
  sku: '',
  unit: '',
  category: '',
  notes: '',
  active: true,
});

const CatalogItems = () => {
  const { companyId } = useCompany();
  const { role } = useRole();
  const canEdit = role === 'owner' || role === 'admin';
  const { items, loading, refresh } = useCatalogItems();

  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items || []).filter((it) => {
      if (!showArchived && it.active === false) return false;
      if (!q) return true;
      const hay = `${it.name || ''} ${it.sku || ''} ${it.category || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query, showArchived]);

  const handleOpenCreate = useCallback(() => {
    setForm(emptyForm());
    setModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((it) => {
    setForm({
      id: it.id,
      name: it.name || '',
      sku: it.sku || '',
      unit: it.unit || '',
      category: it.category || '',
      notes: it.notes || '',
      active: it.active !== false,
    });
    setModalOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const headers = await getAuthHeader();
      const payload = {
        companyId,
        name: form.name.trim(),
        sku: form.sku.trim() || null,
        unit: form.unit.trim() || null,
        category: form.category.trim() || null,
        notes: form.notes.trim() || null,
        active: form.active !== false,
      };
      if (form.id) {
        await axios.put(`${API}/api/catalog-items/${form.id}`, payload, { headers });
        toast.success('Catalog item updated');
      } else {
        await axios.post(`${API}/api/catalog-items/`, payload, { headers });
        toast.success('Catalog item created');
      }
      setModalOpen(false);
      invalidateCatalog(companyId);
      await refresh();
    } catch (err) {
      console.error('[CatalogItems] save error:', err);
      toast.error(err?.response?.data?.detail || 'Failed to save catalog item');
    } finally {
      setSaving(false);
    }
  }, [companyId, form, refresh]);

  const handleArchive = useCallback(async (it) => {
    if (!window.confirm(`Archive "${it.name}"? Existing programs and work orders keep it — it just won't appear as a new choice.`)) return;
    try {
      const headers = await getAuthHeader();
      await axios.delete(`${API}/api/catalog-items/${it.id}`, {
        params: { companyId },
        headers,
      });
      toast.success('Item archived');
      invalidateCatalog(companyId);
      await refresh();
    } catch (err) {
      console.error('[CatalogItems] archive error:', err);
      toast.error(err?.response?.data?.detail || 'Failed to archive item');
    }
  }, [companyId, refresh]);

  const handleRestore = useCallback(async (it) => {
    try {
      const headers = await getAuthHeader();
      await axios.post(`${API}/api/catalog-items/${it.id}/restore`, null, {
        params: { companyId },
        headers,
      });
      toast.success('Item restored');
      invalidateCatalog(companyId);
      await refresh();
    } catch (err) {
      console.error('[CatalogItems] restore error:', err);
      toast.error(err?.response?.data?.detail || 'Failed to restore item');
    }
  }, [companyId, refresh]);

  return (
    <div className="space-y-6" data-testid="catalog-items-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Catalog Items</h2>
          <p className="text-sm text-gray-500 mt-1">
            Reusable products and materials selectable from Service Programs and individual visits.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={handleOpenCreate}
            className="text-white"
            style={{ backgroundColor: 'var(--cb-orange)' }}
            data-testid="catalog-new-item-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Item
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, SKU, or category"
            className="pl-9"
            data-testid="catalog-search-input"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="rounded border-gray-300"
            data-testid="catalog-show-archived-checkbox"
          />
          Show archived
        </label>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--cb-orange)] mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-testid="catalog-empty-state">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {items.length === 0 ? 'No catalog items yet' : 'No items match your filter'}
          </h3>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            {items.length === 0
              ? 'Create products and materials once, then reuse them across Service Programs and individual visits.'
              : 'Try clearing the search or enabling "Show archived".'}
          </p>
          {canEdit && items.length === 0 && (
            <Button
              onClick={handleOpenCreate}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Item
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">SKU</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Unit</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                {canEdit && <th className="w-32" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((it) => (
                <tr
                  key={it.id}
                  className={`hover:bg-gray-50 transition-colors ${it.active === false ? 'bg-gray-50/50' : ''}`}
                  data-testid={`catalog-row-${it.id}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${it.active === false ? 'bg-gray-100' : 'bg-orange-50'}`}>
                        <Package className={`w-4 h-4 ${it.active === false ? 'text-gray-400' : 'text-[var(--cb-orange)]'}`} />
                      </div>
                      <div>
                        <div className={`font-medium ${it.active === false ? 'text-gray-500' : 'text-gray-900'}`}>{it.name}</div>
                        {it.notes && (
                          <div className="text-xs text-gray-500 truncate max-w-[280px]">{it.notes}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{it.sku || <span className="text-gray-300">—</span>}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{it.unit || <span className="text-gray-300">—</span>}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{it.category || <span className="text-gray-300">—</span>}</td>
                  <td className="px-6 py-4">
                    {it.active === false ? (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded" data-testid={`catalog-status-${it.id}`}>Archived</span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded" data-testid={`catalog-status-${it.id}`}>Active</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleOpenEdit(it)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit item"
                          data-testid={`catalog-edit-${it.id}`}
                        >
                          <Pencil className="w-4 h-4 text-gray-500" />
                        </button>
                        {it.active === false ? (
                          <button
                            onClick={() => handleRestore(it)}
                            className="p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Restore item"
                            data-testid={`catalog-restore-${it.id}`}
                          >
                            <ArchiveRestore className="w-4 h-4 text-emerald-600" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleArchive(it)}
                            className="p-2 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Archive item"
                            data-testid={`catalog-archive-${it.id}`}
                          >
                            <Archive className="w-4 h-4 text-amber-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit modal */}
      <Dialog open={modalOpen} onOpenChange={(open) => !open && setModalOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit Catalog Item' : 'New Catalog Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2" data-testid="catalog-item-form">
            <div className="space-y-1">
              <Label htmlFor="ci-name">Name *</Label>
              <Input
                id="ci-name"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g., HEPA Filter 20x25x4"
                data-testid="catalog-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ci-sku">SKU / Code</Label>
                <Input
                  id="ci-sku"
                  value={form.sku}
                  onChange={(e) => setForm(f => ({ ...f, sku: e.target.value }))}
                  placeholder="Optional"
                  data-testid="catalog-form-sku"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ci-unit">Unit</Label>
                <Input
                  id="ci-unit"
                  value={form.unit}
                  onChange={(e) => setForm(f => ({ ...f, unit: e.target.value }))}
                  placeholder="ea, oz, lb, gal…"
                  list="ci-unit-suggestions"
                  data-testid="catalog-form-unit"
                />
                <datalist id="ci-unit-suggestions">
                  {UNIT_SUGGESTIONS.map(u => <option key={u} value={u} />)}
                </datalist>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ci-category">Category</Label>
              <Input
                id="ci-category"
                value={form.category}
                onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                placeholder="Optional grouping label"
                data-testid="catalog-form-category"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ci-notes">Notes</Label>
              <Textarea
                id="ci-notes"
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional internal notes"
                rows={2}
                data-testid="catalog-form-notes"
              />
            </div>
            {form.id && (
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active !== false}
                  onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))}
                  className="rounded border-gray-300"
                  data-testid="catalog-form-active"
                />
                Active (uncheck to archive)
              </label>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              data-testid="catalog-form-save-btn"
            >
              {saving ? 'Saving…' : form.id ? 'Save Changes' : 'Create Item'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CatalogItems;
